import {Paths, Directory, File} from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import {Linking, Platform} from "react-native";
import Alert from "@/lib/Alert";
import * as Sentry from "@sentry/react-native";

/**
 * Formats a video duration from seconds to a readable time string
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (hh:mm:ss, mm:ss, or 0:ss)
 */
export const formatDuration = (seconds: number): string => {
    // Handle edge cases
    if (seconds < 0 || !Number.isFinite(seconds)) {
        return '0:00';
    }

    // Round to nearest whole second
    const totalSeconds = Math.round(seconds);

    // Calculate hours, minutes, and seconds
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    // Format based on duration length
    if (hours > 0) {
        // Include hours: "1:23:45"
        return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
        // No hours: "23:45" or "0:45"
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
};

// Example usage:
// formatDuration(45) => "0:45"
// formatDuration(150) => "2:30"
// formatDuration(3665) => "1:01:05"
// formatDuration(0) => "0:00"
// formatDuration(-10) => "0:00"
// formatDuration(NaN) => "0:00"

/**
 * Calculates and formats the time elapsed since a given date
 * @param assignedDate - ISO date string of when the achievement was assigned
 * @param t - i18next translation function
 * @returns Human-readable time elapsed string
 */
export const getTimeSinceAssigned = (assignedDate?: string, t?: any): string => {
    if (!assignedDate) {
        return '';
    }

    // If no translation function provided, return empty
    if (!t) {
        return '';
    }

    const now = new Date();
    const assigned = new Date(assignedDate);
    const diffTime = Math.abs(now.getTime() - assigned.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return t('common.time.dayAgo');
    if (diffDays < 7) return t('common.time.daysAgo', { count: diffDays });
    if (diffDays < 14) return t('common.time.weekAgo');
    if (diffDays < 30) {
        const weeks = Math.ceil(diffDays / 7);
        return t('common.time.weeksAgo', { count: weeks });
    }
    const months = Math.ceil(diffDays / 30);
    return t('common.time.monthsAgo', { count: months });
};

export function getPriceInfo(offer: {price?: number, currency?: string}) {
    if (!offer) return {price: 0, currency: 'EUR', isFree: true};

    return {
        price: offer.price ? offer.price / 100 : 0,
        currency: offer.currency || 'EUR',
        isFree: !offer.price || offer.price === 0,
    };
}

import {MediaItemType} from "@/data/app-data-interfaces";

const documentMetadata: Record<string, { mime: string; uti: string; ext: string }> = {
    pdf:  { mime: 'application/pdf', uti: 'com.adobe.pdf', ext: '.pdf' },
    docx: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', uti: 'org.openxmlformats.wordprocessingml.document', ext: '.docx' },
    doc:  { mime: 'application/msword', uti: 'com.microsoft.word.doc', ext: '.doc' },
    xlsx: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', uti: 'org.openxmlformats.spreadsheetml.sheet', ext: '.xlsx' },
    xls:  { mime: 'application/vnd.ms-excel', uti: 'com.microsoft.excel.xls', ext: '.xls' },
    pptx: { mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', uti: 'org.openxmlformats.presentationml.presentation', ext: '.pptx' },
    ppt:  { mime: 'application/vnd.ms-powerpoint', uti: 'com.microsoft.powerpoint.ppt', ext: '.ppt' },
    jpg:  { mime: 'image/jpeg', uti: 'public.jpeg', ext: '.jpg' },
    png:  { mime: 'image/png', uti: 'public.png', ext: '.png' },
    gif:  { mime: 'image/gif', uti: 'com.compuserve.gif', ext: '.gif' },
    webp: { mime: 'image/webp', uti: 'public.webp', ext: '.webp' },
    txt:  { mime: 'text/plain', uti: 'public.plain-text', ext: '.txt' },
};

export const isDocumentType = (type: MediaItemType): boolean => type !== 'video' && type !== 'audio';

export const openDocument = async (url: string, name: string, type: MediaItemType = 'pdf') => {
    const meta = documentMetadata[type] || documentMetadata.pdf;

    // --- 1. Web Implementation: Open URL in a new tab ---
    if (Platform.OS === 'web') {
        try {
            await Linking.openURL(url);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error opening document on web:', error);
            Alert.alert('Error', 'Failed to open the document in a new tab.');
        }
        return;
    }

    // --- 2. Mobile (iOS/Android) Implementation: Download and Share ---

    const directory = new Directory(Paths.cache, 'och-documents');
    if (!directory.exists) {
        directory.create();
    }
    const file = new File(directory, `${name}${meta.ext}`);

    try {
        const output = await File.downloadFileAsync(url, file, {idempotent: true})

        if (!output.exists) {
            Alert.alert('Download Error', 'Failed to download document.');
            return;
        }

        console.log('Finished downloading to:', output.uri);

        const isAvailable = await Sharing.isAvailableAsync();

        if (isAvailable) {
            await Sharing.shareAsync(output.uri, {
                mimeType: meta.mime,
                UTI: meta.uti,
            });
        } else {
            Alert.alert(
                'Cannot Open File',
                'Sharing is not available on this device to open the document.'
            );
        }
    } catch (error) {
        Sentry.captureException(error);
        console.error('Error downloading or opening document on mobile:', error);
        Alert.alert('Error', 'An unexpected error occurred while processing the document.');
    }
};

/** @deprecated Use openDocument instead */
export const openPdf = (url: string, name: string) => openDocument(url, name, 'pdf');
